/**
 * SOLID Review
 * S — Strong. One concern: which way a table code leads a player — join, rejoin, late-join, full, or nowhere.
 * O — Good. A new route is one more branch, and one more member of the union.
 * L — N/A — a function.
 * I — Strong. One function, the lookup and the identity in, the route out.
 * D — Strong. Types only.
 */

/**
 * MOVED to the Domain (frontend strict-ports plan, FE2, 2026-10-03) from
 * application/use-cases/SmartJoinByCodeUseCase.ts, whose `execute` was this rule wrapped around a port
 * call and a try/catch. The call is the Application's now and the failure is its outcome; the rule is
 * unchanged, branch for branch. The JOIN route's `roomStatus: 'WAITING'` went: nothing read it.
 */
export type TSmartJoinRoute =
  | { readonly type: 'JOIN'; readonly roomId: string }
  | { readonly type: 'LATE_JOIN'; readonly roomId: string; readonly displayName: string }
  | { readonly type: 'REJOIN'; readonly roomId: string; readonly playerId: string }
  | { readonly type: 'ROOM_FULL' }
  | { readonly type: 'NOT_FOUND' };

/** What the lookup by code tells the rule: the room's state and every member, connected or not. */
export interface IEntitySmartJoinLookup {
  readonly roomId: string;
  readonly roomStatus: string;
  readonly playerCount: number;
  readonly suspendedPlayers: ReadonlyArray<{
    readonly id: string;
    readonly displayName: string;
    readonly email?: string | null;
    readonly isConnected: boolean;
  }>;
}

/** Who is asking. */
export interface IEntitySmartJoinIdentity {
  readonly playerId: string;
  readonly displayName: string;
  readonly email: string | null;
}

/** Maximum seats at a table; a 6th player gets ROOM_FULL. */
const MAX_PLAYERS = 6;

export function smartJoinRoute(lookup: IEntitySmartJoinLookup | null, identity: IEntitySmartJoinIdentity): TSmartJoinRoute {
  if (lookup === null) return { type: 'NOT_FOUND' };
  if (lookup.roomStatus === 'WAITING') return { type: 'JOIN', roomId: lookup.roomId };
  if (lookup.roomStatus !== 'IN_PROGRESS') return { type: 'NOT_FOUND' };

  // Room is IN_PROGRESS. A returning player must be able to rejoin even if
  // the backend still treats their (churning) connection as live — so match
  // against ALL members, not just disconnected slots (a still-"connected"
  // member who landed back at WELCOME otherwise fell through to LATE_JOIN,
  // which throws "already in the room"). id/email identify the SAME person
  // reliably, so they match any member; a bare display-name match is only
  // trusted for a *suspended* slot (the guest re-identification case) to
  // avoid colliding with a different connected player of the same name. The
  // backend resumes the same player and rejects a different person who
  // matches a still-connected member (PLAYER_ALREADY_CONNECTED).
  const members = lookup.suspendedPlayers;
  const suspendedSlots = members.filter((p) => !p.isConnected);

  const matchById = members.find((p) => p.id === identity.playerId);
  const email = identity.email;
  const matchByEmail = !matchById && email
    ? members.find((p) => p.email?.toLowerCase() === email.toLowerCase())
    : null;
  const matchByName = !matchById && !matchByEmail
    ? suspendedSlots.find((p) => p.displayName.toLowerCase() === identity.displayName.toLowerCase())
    : null;

  const match = matchById ?? matchByEmail ?? matchByName;
  if (match) return { type: 'REJOIN', roomId: lookup.roomId, playerId: match.id };

  if (lookup.playerCount >= MAX_PLAYERS) return { type: 'ROOM_FULL' };

  // A genuinely different player joining mid-game (id/email matches already
  // became REJOIN above). If the nickname is already in the table, append
  // '*' (repeatedly) so it's disambiguated — they still join, shown e.g. as
  // "Bob*" / "Bob**" rather than colliding with the existing "Bob".
  const taken = new Set(members.map((p) => p.displayName.toLowerCase()));
  let displayName = identity.displayName;
  while (taken.has(displayName.toLowerCase())) displayName += '*';

  return { type: 'LATE_JOIN', roomId: lookup.roomId, displayName };
}
